import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  PlugZap,
  ScanSearch,
  ShieldCheck,
  Send,
  FileSpreadsheet,
  Layers,
  RefreshCw,
  BellRing,
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { publishedSaftCountries } from '@/data/saftCountries';
import SaftFlag from '@/components/saft/SaftFlag';
/* ------------------------------------------------------------------ */
/*  Content                                                            */
/* ------------------------------------------------------------------ */
const STEPS = [
  {
    icon: PlugZap,
    title: 'Connect to your ERP',
    text: 'Taxera sits inside your ERP landscape (SAP, Oracle, Microsoft Dynamics and others). No file exports, no manual uploads.',
  },
  {
    icon: ScanSearch,
    title: 'Extract only what SAF-T needs',
    text: 'The middleware pulls exactly the data fields each country requires, straight from your live transactions.',
  },
  {
    icon: ShieldCheck,
    title: 'Validate before it leaves',
    text: 'Every file is checked against the official schema and run through quality checks before submission, so problems surface early.',
  },
  {
    icon: Send,
    title: 'Generate and submit',
    text: 'The SAF-T file is generated in the right format for the country and submitted, with a full audit trail.',
  },
];
const BENEFITS = [
  {
    icon: FileSpreadsheet,
    title: 'No spreadsheets in the middle',
    text: 'Data goes from your ERP to the tax authority without anyone re-keying or patching numbers in Excel.',
  },
  {
    icon: Layers,
    title: 'One integration for everything',
    text: 'SAF-T uses the same data as your e-Invoicing and VAT returns, so the numbers agree across all of them.',
  },
  {
    icon: RefreshCw,
    title: 'Reconciled by design',
    text: 'SAF-T is cross-checked against your source transactions and filed returns before submission, which is exactly what authorities now do on their side.',
  },
  {
    icon: BellRing,
    title: 'Format changes are our problem',
    text: 'When a country changes its schema or rules, we track it and update the platform. You do not have to rebuild anything.',
  },
];
const FAQS = [
  {
    q: 'What is SAF-T?',
    a: "SAF-T (Standard Audit File for Tax) is a standardised electronic file containing a company's accounting and tax data. A growing number of tax authorities require companies to submit it, either periodically or on request, so they can check VAT and other tax positions directly from the source data.",
  },
  {
    q: 'Which countries require SAF-T?',
    a: 'Requirements differ by country in scope, file format, frequency and deadlines, and they keep changing. Taxera already supports countries such as Portugal, Norway, Poland and Lithuania. Our free Global Tax Tracker shows the current status country by country.',
  },
  {
    q: 'How does Taxera create the SAF-T file from SAP or another ERP?',
    a: "Taxera's middleware connects to your ERP and extracts exactly the data fields required for each country's SAF-T format. The file is then validated against the official schema and run through pre-submission quality checks before it is submitted.",
  },
  {
    q: 'What happens when a country changes its SAF-T format?',
    a: 'Taxera tracks regulatory changes and absorbs schema updates in the platform. Your team does not have to rebuild mappings or reports for each change.',
  },
  {
    q: 'Do we need a separate tool for every country?',
    a: 'No. Taxera is one platform with one ERP integration. Country-specific formats and rules are handled inside the platform, alongside your e-Invoicing and VAT reporting.',
  },
];
const RELATED = [
  { to: '/tracker/saft', label: 'Live SAF-T changes', note: 'Free Global Tax Tracker' },
  { to: '/products', label: 'e-Invoicing and VAT returns', note: 'Same data, same integration' },
  { to: '/sap-s4hana', label: 'SAP S/4HANA and tax compliance', note: 'Migration without compliance gaps' },
  { to: '/blog', label: 'Latest articles and updates', note: 'Regulatory analysis from our team' },
];
/* ------------------------------------------------------------------ */
/*  Small pieces                                                       */
/* ------------------------------------------------------------------ */
const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`saft-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`saft-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm sm:text-base text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
};
/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
const COUNTRY_FILTERS = [
  { key: 'all', label: 'All countries' },
  { key: 'mandatory', label: 'Mandatory' },
  { key: 'request', label: 'On request' },
  { key: 'voluntary', label: 'Voluntary' },
];
const statusKey = (label) => {
  const l = (label || '').toLowerCase();
  if (l.startsWith('voluntary')) return 'voluntary';
  if (l.startsWith('on request')) return 'request';
  return 'mandatory';
};
const statusDot = (label) => {
  const k = statusKey(label);
  if (k === 'voluntary') return 'bg-amber-400';
  if (k === 'request') return 'bg-slate-400';
  return 'bg-[#0066CC]';
};
const SaftSolutionPage = () => {
  const [countryFilter, setCountryFilter] = useState('all');
  const visibleCountries = publishedSaftCountries().filter(
    (c) => countryFilter === 'all' || statusKey(c.statusLabel) === countryFilter
  );
  // Page-level structured data (FAQ + breadcrumb). Removed when leaving the page.
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://taxera.tech/' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://taxera.tech/products' },
          { '@type': 'ListItem', position: 3, name: 'SAF-T', item: 'https://taxera.tech/solutions/saf-t' },
        ],
      },
    ];
    const nodes = schemas.map((schema, i) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-page-jsonld', `saft-${i}`);
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
  }, []);
  return (
    <PageLayout>
      <div data-testid="saft-solution-page">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-8" data-testid="saft-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-slate-700">Solutions</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600">SAF-T</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#14A3B8] mb-4">SAF-T reporting</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] max-w-4xl">
              SAF-T files, generated straight from your ERP.{' '}
              <span className="text-[#0066CC]">In every country.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Tax authorities increasingly want your accounting data in one standard file. Taxera creates, checks and submits
              it directly from your live ERP data, so your team stops chasing exports and spreadsheets.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="saft-cta-demo"
              >
                Book a demo <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#countries"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0066CC] text-slate-800 font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="saft-cta-countries"
              >
                See requirements by country
              </a>
            </div>
          </motion.div>
        </section>
        {/* SAF-T requirements by country: directly under the hero */}
        {publishedSaftCountries().length > 0 && (
          <section id="countries" className="bg-slate-50 border-y border-slate-100 scroll-mt-24" data-testid="saft-countries">
            <div className="max-w-6xl mx-auto px-6 py-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">SAF-T requirements by country</h2>
              <p className="text-slate-600 mb-8 max-w-2xl">
                Every country does SAF-T a little differently. Pick yours to see who has to file, the key dates and what the file contains.
              </p>
              <div className="mb-6 flex flex-wrap gap-2" role="group" aria-label="Filter by status" data-testid="saft-country-filter">
                {COUNTRY_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setCountryFilter(f.key)}
                    aria-pressed={countryFilter === f.key}
                    className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
                      countryFilter === f.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`saft-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visibleCountries.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/solutions/saf-t/${c.slug}`}
                    className="group relative flex flex-col justify-between min-h-[190px] rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#0066CC] hover:shadow-lg hover:shadow-blue-900/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0066CC] transition-all"
                    data-testid={`saft-country-link-${c.slug}`}
                  >
                    <div className="flex items-start justify-between">
                      <SaftFlag code={c.code} name={c.name} className="h-14 w-14" />
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 group-hover:bg-[#0066CC] group-hover:border-[#0066CC] group-hover:text-white transition-colors">
                        <ArrowRight className="w-4 h-4 -rotate-45 group-hover:rotate-0 transition-transform" />
                      </span>
                    </div>
                    <div className="mt-8">
                      <div className="font-heading text-2xl font-semibold text-slate-900 leading-tight">{c.name}</div>
                      <div className="mt-1 text-sm text-slate-500">{c.schemeLabel}</div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <span className={`h-2 w-2 rounded-full ${statusDot(c.statusLabel)}`} aria-hidden="true" />
                        {c.statusLabel}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <p className="mt-8 text-sm text-slate-500">
                Want to follow changes as they happen? See the <Link to="/tracker/saft" className="text-[#0066CC] font-semibold hover:underline" data-testid="saft-tracker-link">Global Tax Tracker</Link>.
              </p>
            </div>
          </section>
        )}
        {/* What it is */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="saft-what">
          <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">What is SAF-T, in plain words?</h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                SAF-T stands for Standard Audit File for Tax. It is a standardised electronic file with your accounting and
                tax data inside. Authorities use it to check your VAT and other taxes straight from the source data
                instead of waiting for paper reports.
              </p>
              <p>
                Each country decides its own scope, format, frequency and deadlines, and the rules keep changing. For a
                company active in several countries, that quickly becomes a patchwork of exports, scripts and spreadsheets.
              </p>
            </div>
          </div>
        </section>
        {/* How it works */}
        <section className="max-w-6xl mx-auto px-6 py-16" data-testid="saft-how">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">How Taxera does it</h2>
          <p className="text-slate-600 mb-10 max-w-2xl">From your ERP to the tax authority, in four steps you do not have to run by hand.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="rounded-2xl border border-slate-200 bg-white p-6"
                data-testid={`saft-step-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                  </span>
                  <span className="text-xs font-semibold text-slate-400">STEP {i + 1}</span>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </section>
        {/* Why it makes life easier */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="saft-benefits">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">What changes for your team</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07 }}
                  className="flex gap-4 rounded-2xl bg-white border border-slate-200 p-6"
                  data-testid={`saft-benefit-${i + 1}`}
                >
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-[#14A3B8]/10 flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-[#14A3B8]" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="saft-faq">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">SAF-T questions, answered</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </section>
        {/* Related */}
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="saft-related">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Keep reading</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {RELATED.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="group rounded-xl border border-slate-200 p-5 hover:border-[#0066CC]/50 hover:shadow-md transition-all"
                data-testid={`saft-related-${r.to.replace(/\//g, '-').replace(/^-/, '')}`}
              >
                <div className="text-sm font-semibold text-slate-900 group-hover:text-[#0066CC] transition-colors">{r.label}</div>
                <div className="text-xs text-slate-500 mt-1">{r.note}</div>
              </Link>
            ))}
          </div>
        </section>
        {/* Final CTA */}
        <section className="bg-[#1A1A1A] text-white" data-testid="saft-final-cta">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold">See SAF-T running on your own ERP data</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">
              Book a short demo. We will show you the full path from your ERP to the tax authority.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-7 py-3 rounded-xl transition-colors"
              data-testid="saft-final-demo"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};
export default SaftSolutionPage;
